import {HttpClient} from '@angular/common/http';
import {AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild} from '@angular/core';
import {Geoposition} from '@awesome-cordova-plugins/geolocation/ngx';
import {LoadingController, MenuController, ModalController, ToastController} from '@ionic/angular';
import {Observable, Subscription} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {Step} from '../../../../core/api/resources/step.resource';
import {GpxParserService} from '../../../../services/gpx-parser/gpx-parser.service';
import {Gpx} from '../../../../services/gpx-parser/models/gpx.model';
import {MapsHelperService} from '../../../../services/maps-helper/maps-helper.service';
import LatLngLiteral = google.maps.LatLngLiteral;
import {google} from '@google/maps';
import {Geolocation} from '@awesome-cordova-plugins/geolocation/ngx';

declare var mapkit: any;


@Component({
  selector: 'unr-mapkit-map',
  templateUrl: 'mapkit-map.component.html',
  styleUrls: [
    'mapkit-map.component.scss'
  ]
})
export class UnrMapKitMapComponent implements AfterViewInit, OnDestroy {

  @ViewChild('map', {static: false}) mapElement: ElementRef;
  @Input() gpxUrl: string;
  @Input() step: Step;

  map: any;
  userLocationSubscription: Subscription;
  backdrop;
  loader: HTMLIonLoadingElement;
  tokenEndpoint = 'https://token.unrestricted.eu/mk-token';

  constructor(private geoLocation: Geolocation,
              private gpx: GpxParserService,
              private mapsHelper: MapsHelperService,
              private http: HttpClient,
              private loadingController: LoadingController,
              private toastCtrl: ToastController,
              public modalController: ModalController,
              private menuController: MenuController) {
  }

  async ngAfterViewInit(): Promise<void> {
    this.menuController.enable(false, 'sideMenu').then();
    this.presentLoader();
    this.addBackdropDismissClick();
    this.initMapkit();
  }

  dismiss(): void {
    this.modalController.dismiss();
  }

  private getUserLocation(): Promise<void> {
    if (!this.mapsHelper.iosMap) {
      return;
    }
    return this.geoLocation
      .getCurrentPosition()
      .then((position: Geoposition) => {
        if (position.coords) {
          this.drawUserMarker({lat: position.coords.latitude, lng: position.coords.longitude});
        }
      });
  }

  private drawUserMarker(coords: { lat: number; lng: number }): void {
    const startPoint = new mapkit.Coordinate(coords.lat, coords.lng);
    const startPointAnnotation = new mapkit.MarkerAnnotation(startPoint);
    startPointAnnotation.color = '#14213D';

    this.map.addAnnotation(startPointAnnotation);
  }

  private drawBeginPoint(point: [number, number]): void {
    this.map.showItems([this.mapsHelper.getBeginMarker(point)]);
  }

  private drawEndPoint(point: [number, number]): void {
    this.map.showItems([this.mapsHelper.getEndMarker(point)]);
  }

  private drawRouteLines(coords: Array<[number, number]>): void {
    this.map.addOverlay(this.mapsHelper.getRoutePolyLine(coords));
  }

  private drawRoute(): Observable<{} | Gpx> {
    this.createNewMap().then();
    return this.gpx.getGpxFile(this.gpxUrl)
      .pipe(
        tap(gpx => {
          const mapKitPoints: Array<[number, number]> = this.mapsHelper.mapToMapkitPoints(this.gpx.geTrackPoints(gpx));
          if (mapKitPoints.length > 0) {
            const trackPointCount = mapKitPoints.length - 1;
            const beginPoint: [number, number] = [mapKitPoints[0][0], mapKitPoints[0][1]];
            const endPoint: [number, number] = [mapKitPoints[trackPointCount][0], mapKitPoints[trackPointCount][1]];

            this.drawBeginPoint(beginPoint);
            this.drawEndPoint(endPoint);
            this.drawRouteLines(mapKitPoints);
            this.drawStepPoint();

            if (!this.step) {
              this.setRegionAndInitialZoom(mapKitPoints);
            } else if (!this.step.latitude
              || !this.step.longitude) {
              this.setRegionAndInitialZoom(mapKitPoints);
            }
          }
        }),

        tap(() => {
          this.loadingController.dismiss();
        }),
        catchError(async err => {
          this.loadingController.dismiss();
          this.dismiss();
          const toast: HTMLIonToastElement = await this.toastCtrl.create({message: 'Failed downloading map', duration: 3000});
          toast.present();

          throw err;
        })
      );
  }

  private setRegionAndInitialZoom(mapKitPoints: Array<[number, number]>): void {
    this.map.region = this.mapsHelper.getCoordinateRegion(mapKitPoints);
  }

  private drawStepPoint(): void {
    if (this.step) {
      if (!this.step.latitude || !this.step.longitude) {
        return;
      }
      const position: [number, number] = [Number(this.step.latitude), Number(this.step.longitude)];
      this.map.showItems([this.mapsHelper.getStepMarker(position)]);

      const centerCoords = this.mapsHelper.getCenterFromDegrees([position]);
      this.map.region = new mapkit.CoordinateRegion(
        new mapkit.Coordinate(centerCoords[0], centerCoords[1]),

        // Zoom level
        new mapkit.CoordinateSpan(0.1, 0.4)
      );
    }
  }

  private async createNewMap(): Promise<void> {
    this.map = new mapkit.Map(this.mapElement.nativeElement);
    this.mapsHelper.iosMap = this.map;
    this.map.showsZoomControl = true;
    this.map.showsPointsOfInterest = true;
    await this.getUserLocation();
  }

  private async initMapkit(): Promise<void> {
    if (this.mapsHelper.iosMap) {
      this.drawRoute().subscribe();
    } else {
      await mapkit.init({
        authorizationCallback: (done) => {
          this.http.get(this.tokenEndpoint, {responseType: 'text'})
            .pipe(
              tap(done),
              switchMap(() => this.drawRoute())
            )
            .subscribe();
        },
        language: 'en'
      });
    }
  }

  ngOnDestroy(): void {
    if (this.backdrop) {
      this.backdrop.removeAttribute('onclick');
    }
    if (this.userLocationSubscription) {
      this.userLocationSubscription.unsubscribe();
    }
    this.menuController.enable(true, 'sideMenu').then();
  }

  private addBackdropDismissClick(): void {
    this.backdrop = window.document.querySelector('.modal-wrapper');
    if (this.backdrop) {
      this.backdrop.onclick = () => {
        this.dismiss();
      };
    }
  }

  private async presentLoader(): Promise<void> {
    this.loader = await this.loadingController.create({
      spinner: 'crescent' , 
      duration : 3000
    });
    await this.loader.present();
  }
}
